import React, { useState, useRef, useEffect } from 'react';
import Tooltip from './Tooltip';

/**
 * Reusable Export Button with format dropdown
 * Supports CSV, XLSX, and PDF exports
 * Optionally supports multi-section exports via `sections` prop
 * 
 * @param {object} props
 * @param {function} props.onExport - Callback function(format, sectionKey?) when export is clicked
 * @param {string[]} props.formats - Available formats (default: ['csv', 'xlsx', 'pdf'])
 * @param {boolean} props.disabled - Disable the button
 * @param {string} props.tooltipText - Tooltip text for the button
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.label - Button label (default: 'Export')
 * @param {Array<{key: string, label: string}>} props.sections - Optional export sections for multi-table pages
 */
const ExportButton = ({ 
    onExport, 
    formats = ['csv', 'xlsx', 'pdf'],
    disabled = false,
    tooltipText = 'Export data in various formats',
    className = '',
    label = 'Export',
    sections = null
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
                setSelectedSection(null);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    const handleExport = (format, sectionKey) => {
        setShowDropdown(false);
        setSelectedSection(null);
        onExport(format, sectionKey);
    };

    const handleSectionClick = (sectionKey) => {
        setSelectedSection(sectionKey);
    };

    const handleBack = () => {
        setSelectedSection(null);
    };

    const formatLabels = {
        csv: 'CSV',
        xlsx: 'Excel (XLSX)',
        excel: 'Excel (XLSX)',
        pdf: 'PDF'
    };

    const formatIcons = {
        csv: '📄',
        xlsx: '📊',
        excel: '📊',
        pdf: '📑'
    };

    const hasSections = sections && sections.length > 0;

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <Tooltip text={tooltipText}>
                <button
                    onClick={() => { setShowDropdown(!showDropdown); setSelectedSection(null); }}
                    disabled={disabled}
                    className={`
                        flex items-center gap-2 px-4 py-2 rounded
                        bg-blue-600 text-white font-medium
                        hover:bg-blue-700 transition-colors
                        disabled:bg-gray-300 disabled:cursor-not-allowed
                        ${className}
                    `}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>{label}</span>
                    <svg className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </Tooltip>

            {showDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    {hasSections && selectedSection === null ? (
                        /* Step 1: Section picker */
                        <div className="py-1">
                            <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                Choose what to export
                            </div>
                            <button
                                onClick={() => handleSectionClick('all')}
                                className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center gap-2 transition-colors font-medium text-blue-700"
                            >
                                <span className="text-lg">📋</span>
                                <span>All Sections</span>
                                <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                            <div className="border-t border-gray-100 my-1"></div>
                            {sections.map((section) => (
                                <button
                                    key={section.key}
                                    onClick={() => handleSectionClick(section.key)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                >
                                    <span className="text-lg">📊</span>
                                    <span className="text-gray-700 truncate">{section.label}</span>
                                    <svg className="w-4 h-4 ml-auto text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            ))}
                        </div>
                    ) : (
                        /* Step 2: Format picker (for a specific section or no-section mode) */
                        <div className="py-1">
                            {hasSections && (
                                <>
                                    <button
                                        onClick={handleBack}
                                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-1 transition-colors text-gray-500 text-sm"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        <span>Back</span>
                                    </button>
                                    <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                        {selectedSection === 'all' 
                                            ? 'All Sections' 
                                            : sections.find(s => s.key === selectedSection)?.label || 'Export'}
                                    </div>
                                    <div className="border-t border-gray-100 my-1"></div>
                                </>
                            )}
                            {formats.map((format) => (
                                <button
                                    key={format}
                                    onClick={() => handleExport(format, hasSections ? selectedSection : undefined)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                >
                                    <span className="text-lg">{formatIcons[format]}</span>
                                    <span className="text-gray-700">{formatLabels[format] || format.toUpperCase()}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ExportButton;
